import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { incidentManagementAPI } from '@/shared/lib/api';

export interface ApproverUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface Approver {
  id: string;
  userId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  user: ApproverUser;
}

// ==========================================
// APPROVERS - Gestão de Aprovadores
// ==========================================

/**
 * Hook para listar todos os aprovadores ativos
 */
export function useApprovers() {
  return useQuery<Approver[]>({
    queryKey: ['incident-management', 'approvers'],
    queryFn: async () => {
      const res = await incidentManagementAPI.listApprovers();
      return res.data.data || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook para verificar se o usuário logado é aprovador
 */
export function useIsCurrentUserApprover(userId: string | undefined) {
  return useQuery<boolean>({
    queryKey: ['incident-management', 'is-approver', userId],
    queryFn: async () => {
      if (!userId) return false;
      const res = await incidentManagementAPI.checkIsApprover(userId);
      return res.data.data?.isApprover || false;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook para buscar usuários disponíveis para serem aprovadores
 */
export function useAvailableUsers() {
  return useQuery<ApproverUser[]>({
    queryKey: ['incident-management', 'available-users'],
    queryFn: async () => {
      const res = await incidentManagementAPI.getAvailableUsers();
      return res.data.data || [];
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook para adicionar um aprovador
 */
export function useAddApprover() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const res = await incidentManagementAPI.addApprover(userId);
      return res.data.data;
    },
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'approvers'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'available-users'] });
    },
  });
}

/**
 * Hook para remover um aprovador
 */
export function useRemoveApprover() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      await incidentManagementAPI.removeApprover(userId);
    },
    onSuccess: () => {
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'approvers'] });
      queryClient.invalidateQueries({ queryKey: ['incident-management', 'available-users'] });
    },
  });
}
